import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import axios from "axios";
import {
  CheckCircle2, Download, Copy, Share2, Home, Search,
  Plane, ArrowRight, Calendar, User, Clock, ExternalLink, ClipboardList
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { trackEvent } from "@/components/TrackingScripts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderSuccess() {
  const { t } = useTranslation();
  const { bookingRef } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentVerified, setPaymentVerified] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      // Verify Stripe session if session_id is present
      const sessionId = searchParams.get("session_id");
      if (sessionId) {
        try {
          const statusRes = await axios.get(`${API}/payments/session-status?session_id=${sessionId}`);
          if (statusRes.data.payment_status === "paid") {
            setPaymentVerified(true);
          }
        } catch {
          // Non-blocking — we still show the order
        }
      }

      // Fetch order (may need a few seconds for webhook to process)
      let retries = 0;
      const maxRetries = 5;
      while (retries < maxRetries) {
        try {
          const res = await axios.get(`${API}/orders/${bookingRef}`);
          if (res.data && res.data.status === "confirmed") {
            setOrder(res.data);
            const pricing = res.data.pricing || {};
            trackEvent("purchase", {
              transaction_id: bookingRef,
              value: pricing.total || 0,
              currency: "USD",
              items: [{ item_name: "Onward Flight Ticket", item_category: res.data.validity_type, price: pricing.total || 0, quantity: 1 }],
            });
            // Clean up localStorage
            localStorage.removeItem("selectedFlight");
            localStorage.removeItem("selectedReturnFlight");
            localStorage.removeItem("checkoutData");
            localStorage.removeItem("searchParams");
            break;
          }
        } catch {}
        retries++;
        if (retries < maxRetries) await new Promise(r => setTimeout(r, 2000));
      }

      if (!order && retries >= maxRetries) {
        // If order not found after retries but payment was verified, show pending
        if (paymentVerified) {
          setOrder({ booking_reference: bookingRef, status: "processing", flight_data: {} });
        } else {
          toast.error("Order not found");
          navigate("/");
        }
      }
      setLoading(false);
    };
    if (bookingRef) fetchOrder();
  }, [bookingRef, navigate, searchParams]);

  const handleCopyRef = () => {
    navigator.clipboard.writeText(bookingRef);
    toast.success(t("success.copied"));
  };

  const handleShare = () => {
    const url = `${window.location.origin}/verify/${bookingRef}`;
    if (navigator.share) {
      navigator.share({ title: "Flight Reservation", url });
    } else {
      navigator.clipboard.writeText(url);
      toast.success(t("success.link_copied"));
    }
  };

  const handleDownloadPDF = () => {
    window.open(`${API}/orders/${bookingRef}/pdf`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <Header />
        <div className="pt-32 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!order) return null;

  const fd = order.flight_data || {};

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="success-page">
      <SEOHead title={`Booking Confirmed ${bookingRef} | OnwardFlighTickets`} description="Your flight reservation is confirmed." path={`/checkout/success/${bookingRef}`} />
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-6">
          {/* Success Banner */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.2, stiffness: 200 }}
            >
              <CheckCircle2 className="w-20 h-20 text-[#22c55e] mx-auto mb-4" />
            </motion.div>
            <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-[#1e3a5f] mb-2" data-testid="success-title">
              {t("success.title")}
            </h1>
            <p className="text-[#6b7280]">{t("success.subtitle")}</p>
          </motion.div>

          {/* Booking Reference */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white border border-[#e2e8f0] rounded-2xl p-6 text-center mb-6"
          >
            <p className="text-sm text-[#6b7280] mb-2">{t("success.booking_ref")}</p>
            <div className="flex items-center justify-center gap-3">
              <span className="text-4xl font-bold tracking-[0.3em] text-[#1e3a5f] font-['Outfit']" data-testid="booking-reference">
                {bookingRef}
              </span>
              <button
                onClick={handleCopyRef}
                className="p-2 rounded-lg hover:bg-[#f8fafc] transition-colors"
                data-testid="copy-ref-btn"
                title="Copy"
              >
                <Copy className="w-5 h-5 text-[#6b7280]" />
              </button>
            </div>
            <p className="text-xs text-[#6b7280] mt-3">
              {t("success.email_sent", { email: order.passenger_email })}
            </p>
          </motion.div>

          {/* Flight Summary Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white border border-[#e2e8f0] rounded-2xl p-6 mb-6"
            data-testid="success-flight-summary"
          >
            <h3 className="font-['Outfit'] text-base font-semibold text-[#1e3a5f] mb-4">{t("success.flight_summary")}</h3>

            {order.return_flight_data && <p className="text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-3">Outbound</p>}
            <div className="flex items-center justify-between mb-5">
              <div className="text-center">
                <p className="text-2xl font-bold text-[#1e3a5f] font-['Outfit']">{fd.departure_code}</p>
                <p className="text-xs text-[#6b7280]">{fd.departure_time}</p>
              </div>
              <div className="flex-1 flex items-center mx-4">
                <div className="flex-1 h-px bg-[#e2e8f0]" />
                <Plane className="w-5 h-5 text-[#f97316] mx-2 -rotate-45" />
                <div className="flex-1 h-px bg-[#e2e8f0]" />
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-[#1e3a5f] font-['Outfit']">{fd.arrival_code}</p>
                <p className="text-xs text-[#6b7280]">{fd.arrival_time}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-y-3 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#6b7280]" />
                <span className="text-[#1f2937]">{fd.departure_date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#6b7280]" />
                <span className="text-[#1f2937]">{fd.duration_text}</span>
              </div>
              <div className="flex items-center gap-2">
                <Plane className="w-4 h-4 text-[#6b7280]" />
                <span className="text-[#1f2937]">{fd.airline} {fd.flight_number}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-[#6b7280]" />
                <span className="text-[#1f2937]">
                  {order.passengers?.length > 1
                    ? `${order.passenger_title} ${order.passenger_first_name} ${order.passenger_last_name} and ${order.passengers.length - 1} other${order.passengers.length > 2 ? "s" : ""}`
                    : `${order.passenger_title} ${order.passenger_first_name} ${order.passenger_last_name}`}
                </span>
              </div>
            </div>

            {/* Return Flight */}
            {order.return_flight_data && (() => {
              const rfd = order.return_flight_data;
              return (
                <>
                  <div className="border-t border-[#e2e8f0] my-5" />
                  <p className="text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-3">Return</p>
                  <div className="flex items-center justify-between mb-5">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-[#1e3a5f] font-['Outfit']">{rfd.departure_code}</p>
                      <p className="text-xs text-[#6b7280]">{rfd.departure_time}</p>
                    </div>
                    <div className="flex-1 flex items-center mx-4">
                      <div className="flex-1 h-px bg-[#e2e8f0]" />
                      <Plane className="w-5 h-5 text-[#f97316] mx-2 -rotate-45" />
                      <div className="flex-1 h-px bg-[#e2e8f0]" />
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-[#1e3a5f] font-['Outfit']">{rfd.arrival_code}</p>
                      <p className="text-xs text-[#6b7280]">{rfd.arrival_time}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-y-3 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#6b7280]" />
                      <span className="text-[#1f2937]">{rfd.departure_date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Plane className="w-4 h-4 text-[#6b7280]" />
                      <span className="text-[#1f2937]">{rfd.airline} {rfd.flight_number}</span>
                    </div>
                  </div>
                </>
              );
            })()}

            <div className="mt-4 flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-[#f97316]" />
              <span className="text-[#6b7280]">{t("success.valid_until")}:</span>
              <span className="font-medium text-[#1f2937]">{order.validity_expires_at}</span>
            </div>
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8"
          >
            <button
              onClick={handleDownloadPDF}
              className="flex items-center justify-center gap-2 bg-[#f97316] text-white py-3 rounded-xl font-medium hover:bg-[#ea580c] transition-all hover:-translate-y-0.5 hover:shadow-lg"
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4" /> {t("success.download_pdf")}
            </button>
            <button
              onClick={handleShare}
              className="flex items-center justify-center gap-2 bg-[#1e3a5f] text-white py-3 rounded-xl font-medium hover:bg-[#152e4d] transition-all hover:-translate-y-0.5 hover:shadow-lg"
              data-testid="share-link-btn"
            >
              <Share2 className="w-4 h-4" /> {t("success.share_link")}
            </button>
            <button
              onClick={handleCopyRef}
              className="flex items-center justify-center gap-2 bg-white border border-[#e2e8f0] text-[#1f2937] py-3 rounded-xl font-medium hover:bg-[#f8fafc] transition-all"
              data-testid="copy-booking-btn"
            >
              <Copy className="w-4 h-4" /> {t("success.copy_ref")}
            </button>
          </motion.div>

          {/* What's Next */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white border border-[#e2e8f0] rounded-2xl p-6 mb-8"
          >
            <h3 className="font-['Outfit'] text-base font-semibold text-[#1e3a5f] mb-3">{t("success.whats_next")}</h3>
            <ul className="space-y-2 text-sm text-[#6b7280]">
              <li className="flex gap-2"><span className="text-[#f97316]">&bull;</span> {t("success.next_1")}</li>
              <li className="flex gap-2"><span className="text-[#f97316]">&bull;</span> {t("success.next_2", { ref: bookingRef })}</li>
              <li className="flex gap-2"><span className="text-[#f97316]">&bull;</span> {t("success.next_3")}</li>
            </ul>
          </motion.div>

          {/* Phase 4 Navigation Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8"
          >
            <button
              onClick={() => navigate(`/verify/${bookingRef}`)}
              className="flex items-center justify-center gap-2 bg-[#1e3a5f] text-white py-3 rounded-xl font-medium hover:bg-[#152e4d] transition-all hover:-translate-y-0.5 hover:shadow-lg"
              data-testid="view-verification-btn"
            >
              <ExternalLink className="w-4 h-4" /> {t("success.view_verification")}
            </button>
            <button
              onClick={() => navigate("/my-orders")}
              className="flex items-center justify-center gap-2 bg-white border border-[#1e3a5f] text-[#1e3a5f] py-3 rounded-xl font-medium hover:bg-[#f8fafc] transition-all hover:-translate-y-0.5 hover:shadow-lg"
              data-testid="view-my-orders-btn"
            >
              <ClipboardList className="w-4 h-4" /> {t("success.view_my_orders")}
            </button>
          </motion.div>

          {/* Bottom CTAs */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate("/")}
              className="flex-1 flex items-center justify-center gap-2 border border-[#e2e8f0] bg-white text-[#1f2937] py-3 rounded-xl font-medium hover:bg-[#f8fafc] transition-all"
              data-testid="back-home-btn"
            >
              <Home className="w-4 h-4" /> {t("success.back_home")}
            </button>
            <button
              onClick={() => navigate("/")}
              className="flex-1 flex items-center justify-center gap-2 border border-[#f97316] text-[#f97316] py-3 rounded-xl font-medium hover:bg-[#fff7ed] transition-all"
              data-testid="book-another-btn"
            >
              <Search className="w-4 h-4" /> {t("success.book_another")}
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
